/**
 * DataVisuals.jsx — Decision Cockpit (Data Visuals tab)
 *
 * Layout:
 *   [Executive Summary]
 *   [Response Distribution] [Polarization]
 *   [Key Drivers]
 *   [Demographic Explorer — unified Segment + Trend]
 *   [Recommended Actions]
 */

import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tooltip as ShadTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  ReferenceLine, CartesianGrid,
} from 'recharts';
import {
  Loader2, Info, AlertTriangle, HelpCircle, TrendingUp, Activity,
  Sparkles, ArrowRight, Target, Users, RefreshCw, FileDown, ShieldCheck, Lightbulb,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { SEMANTIC_HEX, SENTIMENT_HEX, getSentimentHex, getDeltaHex } from '../lib/colors';
import { formatScore, formatDelta, formatCompact } from '../lib/formatters';
import api from '../lib/api';

// =============================================================================
// PALETTE — re-exports from canonical lib/colors.js for back-compat in this file
// =============================================================================
const SENTIMENT_COLORS = SENTIMENT_HEX;
const getSentimentColor = getSentimentHex;
const getDeltaColor = getDeltaHex;

const CONFIDENCE_TONE = {
  high:   { tone: 'text-positive-soft-foreground bg-positive-soft border-positive-border', label: 'High confidence' },
  medium: { tone: 'text-warning-soft-foreground bg-warning-soft border-warning-border',    label: 'Medium confidence' },
  low:    { tone: 'text-synneutral-soft-foreground bg-synneutral-soft border-synneutral-border', label: 'Low confidence' },
};

// =============================================================================
// MAIN
// =============================================================================
export default function DataVisuals({ simulationId, onSwitchTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!simulationId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/simulations/${simulationId}/data-visuals`, { _silent: true });
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [simulationId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        <span className="ml-2 text-sm text-slate-500">Computing visual analytics...</span>
      </div>
    );
  }

  if (error || !data?.available) {
    return (
      <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg flex items-start gap-2">
        <Info className="w-4 h-4 text-slate-400 mt-0.5" />
        <p className="text-sm text-slate-500">{data?.reason || error || 'Data Visuals not available for this simulation.'}</p>
      </div>
    );
  }

  const { likert, segment_deltas, drivers, continuous_trends, polarization, demographics, response_scale, total_responses, stimulus_name } = data;

  return (
    <div className="space-y-6" data-testid="data-visuals">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">Data Visuals</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {stimulus_name} · {total_responses.toLocaleString()} responses · {response_scale?.length}-point scale
          </p>
        </div>
      </div>

      {/* 1. EXECUTIVE SUMMARY */}
      <ExecutiveSummary
        likert={likert}
        polarization={polarization}
        drivers={drivers}
        segmentDeltas={segment_deltas}
        totalResponses={total_responses}
        onSwitchTab={onSwitchTab}
      />

      {/* 2. Distribution + Polarization */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <DivergingLikertChart likert={likert} responseScale={response_scale} />
        </div>
        <div>
          <PolarizationGauge polarization={polarization} />
        </div>
      </div>

      {/* 3. Drivers */}
      {drivers && drivers.length > 0 && <DriverTornadoChart drivers={drivers} />}

      {/* 4. Demographic Explorer (unified) */}
      {Object.keys(segment_deltas).length > 0 && (
        <DemographicExplorer
          segmentDeltas={segment_deltas}
          continuousTrends={continuous_trends}
          populationMean={likert.mean}
          scaleLength={likert.scale_length}
        />
      )}

      {/* 5. Recommended Actions */}
      <RecommendedActions
        likert={likert}
        polarization={polarization}
        drivers={drivers}
        segmentDeltas={segment_deltas}
        onSwitchTab={onSwitchTab}
      />
    </div>
  );
}

// =============================================================================
// 1. EXECUTIVE SUMMARY ROW — 5 cards: Support / Resistance / Confidence / Top Positive / Top Risk
// =============================================================================
function ExecutiveSummary({ likert, polarization, drivers, segmentDeltas, totalResponses, onSwitchTab }) {
  const dist = likert.distribution || [];
  const favorable = dist.filter(d => d.sentiment === 'positive').reduce((s, d) => s + d.pct, 0);
  const unfavorable = dist.filter(d => d.sentiment === 'negative').reduce((s, d) => s + d.pct, 0);

  // Confidence: derived from polarization + sample size + bimodality
  let confidence = 'high';
  if (polarization?.bimodal || polarization?.level === 'high' || totalResponses < 200) confidence = 'medium';
  if ((polarization?.level === 'high' && polarization?.bimodal) || totalResponses < 50) confidence = 'low';

  // Top positive & top risk segments — find the segment with biggest +/- delta across all dims
  const { topPositive, topRisk } = useMemo(() => {
    let best = null, worst = null;
    Object.entries(segmentDeltas || {}).forEach(([dim, segs]) => {
      segs.forEach(s => {
        if (!best || s.delta > best.delta) best = { ...s, dim };
        if (!worst || s.delta < worst.delta) worst = { ...s, dim };
      });
    });
    return { topPositive: best, topRisk: worst };
  }, [segmentDeltas]);

  // Trend direction vs population (gives ▲/▼ indicators)
  const supportTrend = favorable > unfavorable ? 'up' : favorable < unfavorable ? 'down' : 'flat';

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="executive-summary">
      <SummaryCard
        label="Support"
        value={`${favorable.toFixed(0)}%`}
        sub="favorable + very favorable"
        tone="positive"
        trend={supportTrend === 'up' ? 'up' : null}
        testId="exec-card-support"
      />
      <SummaryCard
        label="Resistance"
        value={`${unfavorable.toFixed(0)}%`}
        sub="unfavorable + very unfavorable"
        tone="negative"
        trend={supportTrend === 'down' ? 'up' : null}
        testId="exec-card-resistance"
      />
      <SummaryCard
        label="Confidence"
        value={confidence === 'high' ? 'High' : confidence === 'medium' ? 'Medium' : 'Low'}
        sub={confidence === 'high' ? 'tight signal · low spread' : confidence === 'medium' ? 'some assumptions' : 'check polarization'}
        tone={confidence === 'high' ? 'positive' : confidence === 'medium' ? 'warning' : 'neutral'}
        testId="exec-card-confidence"
        valueClass="text-[26px]"
      />
      <SummaryCard
        label="Top Positive Segment"
        value={topPositive ? topPositive.segment : '—'}
        sub={topPositive ? `${topPositive.dim.replace(/_/g, ' ')} · ${formatDelta(topPositive.delta, 2)} vs avg` : 'no significant uplift'}
        tone="positive"
        valueClass="text-base"
        testId="exec-card-top-positive"
        onClick={() => onSwitchTab && onSwitchTab('demographic-breakdown')}
      />
      <SummaryCard
        label="Top Risk Segment"
        value={topRisk ? topRisk.segment : '—'}
        sub={topRisk ? `${topRisk.dim.replace(/_/g, ' ')} · ${formatDelta(topRisk.delta, 2)} vs avg` : 'no significant risk'}
        tone="negative"
        valueClass="text-base"
        testId="exec-card-top-risk"
        onClick={() => onSwitchTab && onSwitchTab('demographic-breakdown')}
      />
    </div>
  );
}

function SummaryCard({ label, value, sub, tone = 'neutral', trend, testId, valueClass, onClick }) {
  const toneColor = {
    positive: 'text-positive-soft-foreground',
    negative: 'text-negative-soft-foreground',
    warning:  'text-warning-soft-foreground',
    neutral:  'text-slate-900 dark:text-slate-100',
  }[tone];

  const accentBg = {
    positive: 'before:bg-positive',
    negative: 'before:bg-negative',
    warning:  'before:bg-warning',
    neutral:  'before:bg-slate-300',
  }[tone];

  const Wrap = onClick ? 'button' : 'div';
  return (
    <Wrap
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "relative bg-white dark:bg-slate-800/40 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 text-left transition-all",
        "before:absolute before:left-0 before:top-3 before:bottom-3 before:w-1 before:rounded-r-full",
        accentBg,
        onClick && "hover:shadow-md hover:border-slate-300 dark:hover:border-slate-600 cursor-pointer"
      )}
    >
      <div className="flex items-center justify-between gap-1">
        <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400">{label}</div>
        {trend === 'up' && <TrendingUp className="w-3 h-3 text-slate-400 shrink-0" />}
      </div>
      <div className={cn("font-bold tabular-nums leading-none mt-2 truncate", toneColor, valueClass || 'text-[30px]')} title={String(value)}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5 truncate" title={sub}>{sub}</div>}
    </Wrap>
  );
}

// Auto-insight: 1 short sentence under each chart. Muted italic.
function ChartInsight({ children, testId }) {
  if (!children) return null;
  return (
    <p className="text-[11px] italic text-slate-500 dark:text-slate-400 mt-2 leading-snug" data-testid={testId}>
      {children}
    </p>
  );
}

// =============================================================================
// 2. RESPONSE DISTRIBUTION (vertical bars + sentiment strip + KPI tiles)
// =============================================================================
function DivergingLikertChart({ likert, responseScale }) {
  if (!likert?.distribution) return null;
  const dist = likert.distribution;
  const scaleLen = responseScale.length;
  const midpoint = Math.floor(scaleLen / 2);
  const hasOddScale = scaleLen % 2 === 1;

  let negPct = 0, neuPct = 0, posPct = 0;
  dist.forEach((d, i) => {
    if (hasOddScale && i === midpoint) neuPct += d.pct;
    else if (i < midpoint) negPct += d.pct;
    else posPct += d.pct;
  });

  const boxSize = scaleLen >= 5 ? 2 : 1;
  const topBox = dist.slice(-boxSize).reduce((s, d) => s + d.pct, 0);
  const bottomBox = dist.slice(0, boxSize).reduce((s, d) => s + d.pct, 0);

  const mode = dist.reduce((m, d) => (d.pct > m.pct ? d : m), dist[0]);

  let cum = 0;
  let median = likert.mean;
  for (const d of dist) {
    cum += d.pct;
    if (cum >= 50) { median = d.score; break; }
  }

  const skew = posPct - negPct;

  const chartData = dist.map((d, i) => ({
    name: d.option,
    pct: d.pct,
    count: d.count,
    fill: getSentimentColor(d.sentiment, i, scaleLen),
  }));

  const maxPct = Math.max(...dist.map(d => d.pct), 1);
  const yMax = Math.min(100, Math.ceil((maxPct + 5) / 5) * 5);

  return (
    <Card className="border border-slate-200 dark:border-slate-700 shadow-sm h-full" data-testid="diverging-likert">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base font-bold text-slate-900 dark:text-slate-100">Response Distribution</CardTitle>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {likert.total.toLocaleString()} responses · {scaleLen}-point scale
            </p>
          </div>
          <Badge variant="outline" className={cn(
            "text-[10px] font-semibold",
            skew > 5 ? "text-positive-soft-foreground bg-positive-soft border-positive-border" :
            skew < -5 ? "text-negative-soft-foreground bg-negative-soft border-negative-border" :
            "text-slate-600 bg-slate-50 border-slate-200"
          )} data-testid="dist-skew-badge">
            {skew > 5 ? `Skews positive (+${skew.toFixed(0)} pp)` :
             skew < -5 ? `Skews negative (${skew.toFixed(0)} pp)` :
             'Balanced'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Auto-insight */}
        <ChartInsight testId="dist-insight">
          {(() => {
            if (skew > 10) return `Result skews favorable — ${posPct.toFixed(0)}% positive vs ${negPct.toFixed(0)}% negative.`;
            if (skew < -10) return `Result skews unfavorable — ${negPct.toFixed(0)}% negative vs ${posPct.toFixed(0)}% positive.`;
            if (mode && mode.pct > 35) return `"${mode.option}" dominates with ${mode.pct.toFixed(0)}% — opinion concentrates on this option.`;
            return `Opinions are spread — top option "${mode.option}" only at ${mode.pct.toFixed(0)}%.`;
          })()}
        </ChartInsight>
        <div style={{ height: 200 }} data-testid="dist-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 18, right: 8, left: -10, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} interval={0} />
              <YAxis domain={[0, yMax]} tickFormatter={v => `${v}%`} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={40} />
              <ReferenceLine y={100 / scaleLen} stroke="#cbd5e1" strokeDasharray="3 3" label={{ value: 'uniform', fontSize: 9, fill: '#94a3b8', position: 'right' }} />
              <Tooltip
                cursor={{ fill: 'rgba(148,163,184,0.08)' }}
                content={({ active, payload }) => {
                  if (!active || !payload?.[0]) return null;
                  const d = payload[0].payload;
                  return (
                    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 shadow-md rounded px-3 py-2 text-xs">
                      <p className="font-semibold text-slate-800 dark:text-slate-100">{d.name}</p>
                      <p className="text-slate-600 dark:text-slate-300">
                        <span className="font-mono font-semibold">{d.pct.toFixed(1)}%</span>
                        <span className="text-slate-400 ml-1">({d.count.toLocaleString()})</span>
                      </p>
                    </div>
                  );
                }}
              />
              <Bar dataKey="pct" radius={[6, 6, 0, 0]} maxBarSize={64}>
                {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div data-testid="dist-sentiment-strip">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-1.5">
            <span>Negative</span>
            <span>Neutral</span>
            <span>Positive</span>
          </div>
          <div className="flex h-2 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800">
            {negPct > 0 && <div className="h-full" style={{ width: `${negPct}%`, backgroundColor: SENTIMENT_COLORS.strongly_negative }} />}
            {neuPct > 0 && <div className="h-full" style={{ width: `${neuPct}%`, backgroundColor: SENTIMENT_COLORS.neutral }} />}
            {posPct > 0 && <div className="h-full" style={{ width: `${posPct}%`, backgroundColor: SENTIMENT_COLORS.strongly_positive }} />}
          </div>
          <div className="flex items-center justify-between text-[11px] mt-1 font-mono">
            <span className="text-negative-soft-foreground">{negPct.toFixed(0)}%</span>
            <span className="text-slate-500">{neuPct.toFixed(0)}%</span>
            <span className="text-positive-soft-foreground">{posPct.toFixed(0)}%</span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="dist-kpis">
          <KpiTile label="Top-Box" value={`${topBox.toFixed(0)}%`} sub={dist.slice(-boxSize).map(d => d.option).join(' / ')} tone="positive" testId="kpi-top-box" />
          <KpiTile label="Bottom-Box" value={`${bottomBox.toFixed(0)}%`} sub={dist.slice(0, boxSize).map(d => d.option).join(' / ')} tone="negative" testId="kpi-bottom-box" />
          <KpiTile label="Net Score" value={`${likert.net_support > 0 ? '+' : ''}${likert.net_support.toFixed(0)}`} sub="positive − negative" tone={likert.net_support > 0 ? 'positive' : likert.net_support < 0 ? 'negative' : 'neutral'} testId="kpi-net-score" />
          <KpiTile label="Mean" value={formatScore(likert.mean, 1)} sub={`SD ${formatScore(likert.std, 1)}`} tone="neutral" testId="kpi-mean" />
          <KpiTile label="Most common" value={mode.option} sub={`${mode.pct.toFixed(0)}% · median ${typeof median === 'number' ? median.toFixed(0) : median}`} tone="neutral" valueClass="text-sm" testId="kpi-mode" />
        </div>

        <div className="flex items-center justify-center gap-x-4 gap-y-1.5 flex-wrap pt-2 border-t border-slate-100 dark:border-slate-800">
          {dist.map((d, i) => (
            <div key={d.option} className="flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-300">
              <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: getSentimentColor(d.sentiment, i, scaleLen) }} />
              <span className="font-medium">{d.option}</span>
              <span className="text-slate-400 font-mono">{d.pct.toFixed(0)}%</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function KpiTile({ label, value, sub, tone = 'neutral', valueClass, testId }) {
  const tones = {
    positive: 'bg-positive-soft border-positive-border text-positive-soft-foreground',
    negative: 'bg-negative-soft border-negative-border text-negative-soft-foreground',
    neutral:  'bg-slate-50 border-slate-200 text-slate-700 dark:bg-slate-800/40 dark:border-slate-700 dark:text-slate-200',
  };
  return (
    <div className={cn("rounded-lg border px-3 py-2", tones[tone])} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</div>
      <div className={cn("font-bold tabular-nums leading-tight mt-0.5 truncate", valueClass || 'text-lg')} title={String(value)}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 truncate" title={sub}>{sub}</div>}
    </div>
  );
}

// =============================================================================
// 3. KEY DRIVERS — Top-3 + High/Medium/Low Confidence
// =============================================================================
function DriverTornadoChart({ drivers }) {
  const [expanded, setExpanded] = useState(false);
  if (!drivers || drivers.length === 0) return null;

  const TOP_N = 3;
  const visibleDrivers = expanded ? drivers : drivers.slice(0, TOP_N);
  const hasMore = drivers.length > TOP_N;
  const maxEta = Math.max(...drivers.map(d => d.eta_squared), 0.01);

  // Map strength → confidence label
  const confidenceFor = (strength) => strength === 'strong' ? 'high' : strength === 'moderate' ? 'medium' : 'low';

  return (
    <TooltipProvider>
      <Card className="border border-slate-200 dark:border-slate-700 shadow-sm" data-testid="tornado-driver">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-slate-500" />
              <CardTitle className="text-base font-bold text-slate-900 dark:text-slate-100">Key Drivers</CardTitle>
              <ShadTooltip>
                <TooltipTrigger asChild>
                  <button className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="variance-driver-help">
                    <HelpCircle className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs text-xs leading-relaxed p-3">
                  <p className="font-semibold mb-1">Driver Confidence (eta-squared)</p>
                  <p>Measures how much a demographic variable contributes to response variation. Higher = stronger signal.</p>
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /><span><b>High</b> (≥6%) — strong, reliable driver</span></div>
                    <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-sm bg-amber-500" /><span><b>Medium</b> (2–6%) — meaningful</span></div>
                    <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-sm bg-slate-400" /><span><b>Low</b> (&lt;2%) — minimal effect</span></div>
                  </div>
                </TooltipContent>
              </ShadTooltip>
            </div>
            <p className="text-xs text-slate-400 hidden sm:block">
              {drivers.length} demographic dimension{drivers.length === 1 ? '' : 's'} analyzed
            </p>
          </div>
          <ChartInsight testId="drivers-insight">
            {(() => {
              const top = drivers[0];
              if (!top) return null;
              const dimName = top.dimension.replace(/_/g, ' ');
              if (top.eta_squared >= 0.06) return `${dimName} is the strongest driver — ${(top.eta_squared * 100).toFixed(0)}% of variance explained.`;
              if (top.eta_squared >= 0.02) return `${dimName} has a meaningful effect (${(top.eta_squared * 100).toFixed(1)}% of variance) — others are minimal.`;
              return 'No demographic dimension strongly drives responses — opinion is broadly distributed.';
            })()}
          </ChartInsight>
        </CardHeader>
        <CardContent>
          <div className="space-y-2.5">
            {visibleDrivers.map((d, i) => {
              const barWidth = (d.eta_squared / maxEta) * 100;
              const confidence = confidenceFor(d.strength);
              const isTop = i === 0;

              return (
                <div
                  key={d.dimension}
                  className={cn(
                    "flex items-center gap-3 py-3 px-4 rounded-lg border transition-all",
                    isTop ? "bg-slate-50/80 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700 shadow-sm" : "border-transparent hover:bg-slate-50/50 dark:hover:bg-slate-800/20"
                  )}
                  data-testid={`driver-row-${d.dimension}`}
                >
                  {/* Rank */}
                  <span className={cn(
                    "text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center shrink-0",
                    isTop ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" : "bg-slate-100 text-slate-500 dark:bg-slate-800"
                  )}>
                    {i + 1}
                  </span>

                  {/* Dimension name */}
                  <div className="w-36 shrink-0">
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 capitalize truncate">
                      {d.dimension.replace(/_/g, ' ')}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{d.groups} groups</div>
                  </div>

                  {/* Bar */}
                  <div className="flex-1 h-7 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden relative">
                    <div
                      className="h-full rounded-full transition-all duration-700 ease-out flex items-center justify-end pr-3"
                      style={{
                        width: `${Math.max(barWidth, 8)}%`,
                        background:
                          confidence === 'high' ? 'linear-gradient(90deg, #6ee7b7, #059669)' :
                          confidence === 'medium' ? 'linear-gradient(90deg, #fde68a, #f59e0b)' :
                          'linear-gradient(90deg, #e2e8f0, #94a3b8)',
                      }}
                    >
                      {barWidth > 25 && (
                        <span className="text-[11px] font-bold text-white drop-shadow-sm">
                          {(d.eta_squared * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                    {barWidth <= 25 && (
                      <span className="absolute top-1/2 -translate-y-1/2 text-[11px] font-semibold text-slate-600 dark:text-slate-300" style={{ left: `calc(${Math.max(barWidth, 8)}% + 8px)` }}>
                        {(d.eta_squared * 100).toFixed(1)}%
                      </span>
                    )}
                  </div>

                  {/* Confidence badge */}
                  <Badge
                    variant="outline"
                    className={cn("text-[10px] font-semibold shrink-0", CONFIDENCE_TONE[confidence].tone)}
                    data-testid={`driver-confidence-${d.dimension}`}
                  >
                    {confidence === 'high' ? 'High' : confidence === 'medium' ? 'Medium' : 'Low'}
                  </Badge>
                </div>
              );
            })}
            {hasMore && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="w-full py-2 text-xs font-medium text-slate-500 hover:text-blue-700 dark:hover:text-blue-300 transition-colors border-t border-slate-100 dark:border-slate-700 mt-2"
                data-testid="drivers-expand-toggle"
              >
                {expanded ? `Show top ${TOP_N}` : `View all ${drivers.length} drivers →`}
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}

// =============================================================================
// 4. DEMOGRAPHIC EXPLORER — unified Segment + Trend with shared filter
// =============================================================================
function DemographicExplorer({ segmentDeltas, continuousTrends, populationMean, scaleLength }) {
  const segmentDims = Object.keys(segmentDeltas);
  const trendDims = Object.keys(continuousTrends || {});
  const allDims = Array.from(new Set([...segmentDims, ...trendDims]));
  const [selectedDim, setSelectedDim] = useState(allDims[0] || '');

  // Build a unified "ranked rows" list — prefer segment_deltas (categorical groups),
  // fall back to continuous_trends bins for numeric dimensions.
  const segments = segmentDeltas[selectedDim] || [];
  const trend = continuousTrends?.[selectedDim];

  const rankedRows = useMemo(() => {
    if (segments.length > 0) {
      return segments.map(s => ({ label: s.segment, mean: populationMean + s.delta, delta: s.delta, n: s.n }));
    }
    if (trend) {
      return trend.bins.map(b => ({ label: b.label, mean: b.mean, delta: b.mean - populationMean, n: b.n }));
    }
    return [];
  }, [segments, trend, populationMean]);

  // Sort highest mean → lowest (decision-grade ranking)
  const sorted = useMemo(() => [...rankedRows].sort((a, b) => b.mean - a.mean), [rankedRows]);

  // Auto-insight
  const insight = useMemo(() => {
    if (sorted.length === 0) return null;
    const top = sorted[0];
    const bottom = sorted[sorted.length - 1];
    const spread = top.mean - bottom.mean;
    const dimLabel = selectedDim.replace(/_/g, ' ');
    if (spread < 0.15) {
      return `Differences are limited across ${dimLabel} — segments range only ${formatScore(spread, 1)} pts.`;
    }
    if (top.delta > 0.15) {
      return `${top.label} shows the strongest positivity (${formatDelta(top.delta, 2)} vs avg) while ${bottom.label} lags (${formatDelta(bottom.delta, 2)}).`;
    }
    if (bottom.delta < -0.15) {
      return `${bottom.label} is most resistant — ${formatDelta(bottom.delta, 2)} below average.`;
    }
    return `Modest gradient across ${dimLabel} — top segment ${formatDelta(spread, 2)} above bottom.`;
  }, [sorted, selectedDim]);

  return (
    <Card className="border border-slate-200 dark:border-slate-700 shadow-sm" data-testid="demographic-explorer">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-500" />
            <CardTitle className="text-base font-bold text-slate-900 dark:text-slate-100">Demographic Explorer</CardTitle>
          </div>
          <Select value={selectedDim} onValueChange={setSelectedDim}>
            <SelectTrigger className="w-44 h-8 text-xs" data-testid="explorer-dim-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {allDims.map(d => (
                <SelectItem key={d} value={d}>{d.replace(/_/g, ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-[11px] text-slate-400 mt-1">
          Segments ranked by mean response · baseline {formatScore(populationMean, 1)} / {scaleLength}
        </p>
        <ChartInsight testId="explorer-insight">{insight}</ChartInsight>
      </CardHeader>
      <CardContent>
        <SegmentRanking rows={sorted} populationMean={populationMean} scaleLength={scaleLength} />
      </CardContent>
    </Card>
  );
}

function SegmentRanking({ rows, populationMean, scaleLength }) {
  if (!rows || rows.length === 0) return null;
  // Bar width based on mean within scale [1..scaleLength]
  const minVal = 1;
  const maxVal = scaleLength;
  const baselinePct = ((populationMean - minVal) / (maxVal - minVal)) * 100;

  return (
    <div data-testid="segment-ranking">
      <div className="space-y-1.5">
        {rows.map((r) => {
          const meanPct = ((r.mean - minVal) / (maxVal - minVal)) * 100;
          const barColor = getDeltaColor(r.delta);
          const isPositive = r.delta > 0;
          return (
            <div key={r.label} className="flex items-center gap-3 group" data-testid={`rank-row-${r.label}`}>
              {/* Segment label */}
              <div className="w-24 shrink-0 text-right">
                <div className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate" title={r.label}>{r.label}</div>
                <div className="text-[10px] text-slate-400 font-mono">n={formatCompact(r.n)}</div>
              </div>
              {/* Bar */}
              <div className="flex-1 relative h-7 bg-slate-50 dark:bg-slate-800/50 rounded">
                {/* Baseline marker */}
                <div
                  className="absolute top-0 bottom-0 w-px bg-slate-400 dark:bg-slate-500 z-10"
                  style={{ left: `${baselinePct}%` }}
                  title={`baseline ${formatScore(populationMean, 1)}`}
                />
                {/* Bar fill */}
                <div
                  className="absolute top-1 bottom-1 left-0 rounded transition-all duration-500 group-hover:brightness-95"
                  style={{ width: `${meanPct}%`, backgroundColor: barColor, opacity: 0.92 }}
                />
              </div>
              {/* Value */}
              <div className="w-24 shrink-0 flex items-center justify-end gap-2 text-xs">
                <span className="font-bold tabular-nums text-slate-800 dark:text-slate-100 text-sm">
                  {formatScore(r.mean, 2)}
                </span>
                <span className={cn(
                  "font-mono text-[10px] tabular-nums",
                  isPositive ? "text-positive-soft-foreground" : r.delta < 0 ? "text-negative-soft-foreground" : "text-slate-400"
                )}>
                  {formatDelta(r.delta, 2)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {/* Axis footer */}
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100 dark:border-slate-800 px-1 text-[10px] font-mono text-slate-400">
        <span>{minVal}</span>
        <span className="font-semibold text-slate-500">baseline {formatScore(populationMean, 1)}</span>
        <span>{maxVal}</span>
      </div>
    </div>
  );
}

// =============================================================================
// 5. POLARIZATION GAUGE
// =============================================================================
function PolarizationGauge({ polarization }) {
  if (!polarization) return null;
  const { score, level, extreme_pct, std, normalized_entropy, bimodal } = polarization;
  const levelColor = level === 'high' ? 'text-red-600' : level === 'moderate' ? 'text-amber-600' : 'text-emerald-600';
  const levelBg = level === 'high' ? 'bg-red-50' : level === 'moderate' ? 'bg-amber-50' : 'bg-emerald-50';

  return (
    <Card className="border border-slate-200 dark:border-slate-700 shadow-sm h-full" data-testid="polarization-gauge">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-slate-500" />
          <CardTitle className="text-base font-bold text-slate-900 dark:text-slate-100">Polarization</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-center">
          <p className={cn("text-5xl font-black tabular-nums tracking-tight", levelColor)}>{score.toFixed(0)}</p>
          <div className="flex items-center justify-center gap-1.5 mt-1">
            <span className="text-xs text-slate-400">/ 100</span>
            <Badge variant="outline" className={cn("text-[10px] capitalize", levelBg, levelColor)}>{level}</Badge>
          </div>
        </div>

        <div className="space-y-1">
          <div className="h-3 bg-gradient-to-r from-emerald-100 via-amber-100 to-red-100 rounded-full overflow-hidden relative">
            <div
              className="absolute top-0 left-0 h-full rounded-full transition-all duration-700 opacity-80"
              style={{
                width: `${score}%`,
                background: level === 'high' ? 'linear-gradient(90deg, #fbbf24, #ef4444)' :
                  level === 'moderate' ? 'linear-gradient(90deg, #34d399, #fbbf24)' :
                  'linear-gradient(90deg, #6ee7b7, #34d399)'
              }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white border-2 border-slate-700 shadow-md z-10"
              style={{ left: `calc(${Math.min(95, Math.max(5, score))}% - 7px)` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-slate-400">
            <span>Low</span>
            <span>Moderate</span>
            <span>High</span>
          </div>
        </div>

        <div className="space-y-2.5 pt-2 border-t border-slate-100 dark:border-slate-700">
          <MetricRow label="Extreme responses" value={`${extreme_pct.toFixed(0)}%`} hint="Responded at either extreme end" />
          <MetricRow label="Std. Deviation" value={std.toFixed(2)} hint="Spread of responses" />
          <MetricRow label="Entropy" value={`${normalized_entropy.toFixed(0)}%`} sub="of max" hint="How evenly distributed responses are" />
          {bimodal && (
            <div className="flex items-center gap-1.5 text-xs text-amber-600 mt-1 px-2 py-1.5 bg-amber-50 rounded-md">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span className="font-medium">Bimodal distribution detected</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function MetricRow({ label, value, sub, hint }) {
  return (
    <TooltipProvider>
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1">
          <span className="text-slate-500 dark:text-slate-400">{label}</span>
          {hint && (
            <ShadTooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="w-3 h-3 text-slate-300 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="text-xs max-w-[200px]">{hint}</TooltipContent>
            </ShadTooltip>
          )}
        </div>
        <span className="font-mono font-medium text-slate-700 dark:text-slate-200">
          {value}
          {sub && <span className="text-slate-400 ml-1 font-normal">{sub}</span>}
        </span>
      </div>
    </TooltipProvider>
  );
}

// =============================================================================
// 6. RECOMMENDED ACTIONS
// =============================================================================
function RecommendedActions({ likert, polarization, drivers, segmentDeltas, onSwitchTab }) {
  const actions = useMemo(() => {
    const out = [];

    // Action 1: based on polarization
    if (polarization?.level === 'high' || polarization?.bimodal) {
      out.push({
        icon: 'message',
        title: 'Re-test message wording',
        desc: 'Opinions are polarized — try a softer or alternative framing to reduce extreme reactions.',
        cta: 'Run scenario',
        action: () => onSwitchTab && onSwitchTab('scenario'),
      });
    } else {
      out.push({
        icon: 'compare',
        title: 'Compare against alternative scenarios',
        desc: 'Test before/after framing or messaging variants to validate this result.',
        cta: 'Run scenario',
        action: () => onSwitchTab && onSwitchTab('scenario'),
      });
    }

    // Action 2: based on top driver
    const topDriver = drivers?.[0];
    if (topDriver && topDriver.eta_squared >= 0.02) {
      const dimName = topDriver.dimension.replace(/_/g, ' ');
      out.push({
        icon: 'segment',
        title: `Drill into ${dimName}`,
        desc: `${dimName} explains ${(topDriver.eta_squared * 100).toFixed(1)}% of variance — analyze that segment in depth.`,
        cta: 'Open Demographic Breakdown',
        action: () => onSwitchTab && onSwitchTab('demographic-breakdown'),
      });
    } else {
      out.push({
        icon: 'segment',
        title: 'Slice by another demographic',
        desc: 'No single demographic strongly drives responses — explore breakdowns to find hidden patterns.',
        cta: 'Open Demographic Breakdown',
        action: () => onSwitchTab && onSwitchTab('demographic-breakdown'),
      });
    }

    // Action 3: most divergent segment → focused interview
    let bestSeg = null;
    Object.entries(segmentDeltas || {}).forEach(([dim, segs]) => {
      segs.forEach(s => {
        if (!bestSeg || Math.abs(s.delta) > Math.abs(bestSeg.delta)) bestSeg = { ...s, dim };
      });
    });
    if (bestSeg && Math.abs(bestSeg.delta) >= 0.1) {
      out.push({
        icon: 'interview',
        title: `Interview "${bestSeg.segment}"`,
        desc: `Most divergent segment (${bestSeg.delta > 0 ? '+' : ''}${bestSeg.delta.toFixed(2)} from avg) — talk to a virtual respondent to understand why.`,
        cta: 'Start interview',
        action: () => onSwitchTab && onSwitchTab('interview'),
      });
    } else {
      out.push({
        icon: 'interview',
        title: 'Interview a virtual respondent',
        desc: 'Get qualitative depth on the quantitative signal — chat with a synthetic individual.',
        cta: 'Start interview',
        action: () => onSwitchTab && onSwitchTab('interview'),
      });
    }

    // Action 4: always — export
    out.push({
      icon: 'export',
      title: 'Export leadership summary',
      desc: 'Download a polished PDF or PPTX briefing with the key findings for stakeholders.',
      cta: 'Export report',
      action: () => onSwitchTab && onSwitchTab('export'),
    });

    return out;
  }, [polarization, drivers, segmentDeltas, onSwitchTab]);

  const iconFor = (key) => {
    const map = { message: RefreshCw, compare: GitBranch, segment: Users, interview: Lightbulb, export: FileDown };
    return map[key] || Lightbulb;
  };

  return (
    <Card className="border border-slate-200 dark:border-slate-700 shadow-sm" data-testid="recommended-actions">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-amber-500" />
          <CardTitle className="text-base font-bold text-slate-900 dark:text-slate-100">Recommended Actions</CardTitle>
        </div>
        <p className="text-[11px] text-slate-400 mt-0.5">Suggested next steps based on this simulation&apos;s pattern</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {actions.map((a, i) => {
            const Icon = iconFor(a.icon);
            return (
              <button
                key={i}
                onClick={a.action}
                className="text-left p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/30 hover:border-blue-300 hover:shadow-md hover:bg-blue-50/30 dark:hover:bg-blue-950/20 transition-all group"
                data-testid={`recommended-action-${i}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0 group-hover:bg-blue-100 dark:group-hover:bg-blue-900 transition-colors">
                    <Icon className="w-4 h-4 text-slate-600 dark:text-slate-300 group-hover:text-blue-700 dark:group-hover:text-blue-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 leading-tight">{a.title}</div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-snug">{a.desc}</p>
                    <div className="flex items-center gap-1 mt-2 text-[11px] font-semibold text-blue-700 dark:text-blue-400 group-hover:gap-2 transition-all">
                      {a.cta}
                      <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// Lightweight inline icon for "compare" since lucide GitBranch import is local
function GitBranch(props) {
  return (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="6" y1="3" x2="6" y2="15" />
      <circle cx="18" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M18 9a9 9 0 0 1-9 9" />
    </svg>
  );
}
